import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  House,
  Buildings,
  Flame,
  Drop,
  Lightning,
  Question,
  User,
  Users,
  Key,
  ArrowRight,
  ArrowLeft,
  CheckCircle,
  Phone,
  Sparkle,
  CurrencyEur,
} from "@phosphor-icons/react";
import { submitEligibility } from "../lib/api";
import { toast } from "sonner";

const TOTAL_STEPS = 5;

const STATUS_OPTIONS = [
  { value: "proprietaire_occupant", label: "Propriétaire occupant", icon: User, desc: "Vous habitez votre logement" },
  { value: "proprietaire_bailleur", label: "Propriétaire bailleur", icon: Users, desc: "Vous louez votre logement" },
  { value: "locataire", label: "Locataire", icon: Key, desc: "Vous louez votre logement" },
];

const HOUSING_OPTIONS = [
  { value: "maison", label: "Maison individuelle", icon: House, desc: "Maison avec jardin ou terrain" },
  { value: "appartement", label: "Appartement", icon: Buildings, desc: "Logement en immeuble" },
];

const HEATING_OPTIONS = [
  { value: "fioul", label: "Fioul", icon: Drop, desc: "Chaudière fioul" },
  { value: "gaz", label: "Gaz", icon: Flame, desc: "Chaudière gaz" },
  { value: "electrique", label: "Électrique", icon: Lightning, desc: "Convecteurs ou radiateurs" },
  { value: "autre", label: "Autre", icon: Question, desc: "Bois, granulés, autre" },
];

const INCOME_OPTIONS = [
  { value: "moins_20k", label: "Moins de 20 000 €" },
  { value: "20k_40k", label: "Entre 20 000 € et 40 000 €" },
  { value: "plus_40k", label: "Plus de 40 000 €" },
];

export default function EligibilityForm() {
  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({
    status: "",
    housing_type: "",
    heating_type: "",
    postal_code: "",
    income_range: "",
    name: "",
    phone: "",
  });

  const progress = (step / (TOTAL_STEPS + 1)) * 100;

  const next = () => setStep((s) => Math.min(s + 1, TOTAL_STEPS + 1));
  const back = () => setStep((s) => Math.max(s - 1, 1));

  const choose = (field, value) => {
    setData((d) => ({ ...d, [field]: value }));
    setTimeout(() => next(), 180);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!data.name.trim() || !data.phone.trim()) {
      toast.error("Merci de renseigner votre nom et téléphone.");
      return;
    }
    setLoading(true);
    try {
      await submitEligibility(data);
      setSubmitted(true);
      toast.success("Demande envoyée. Un expert vous rappellera rapidement.");
    } catch (err) {
      toast.error("Une erreur est survenue. Merci de réessayer.");
    } finally {
      setLoading(false);
    }
  };

  const canProceedCP = data.postal_code && data.postal_code.length >= 4;

  if (submitted) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        data-testid="eligibility-success"
        className="bg-white p-8 md:p-12 rounded-2xl shadow-[0_8px_30px_rgba(0,0,0,0.06)] border border-slate-100 text-center"
      >
        <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-emerald-100 flex items-center justify-center">
          <CheckCircle size={36} weight="fill" color="#10B981" />
        </div>
        <h3 className="font-heading text-2xl md:text-3xl font-bold text-slate-900 mb-3">
          Merci, votre demande a bien été enregistrée
        </h3>
        <p className="text-slate-600 mb-6 max-w-xl mx-auto leading-relaxed">
          Un expert va vous rappeler rapidement pour échanger sur votre projet,
          vous présenter les solutions adaptées et les aides potentiellement
          mobilisables selon votre situation.
        </p>
        <a
          href="tel:+33610702620"
          data-testid="success-call-btn"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-semibold btn-primary-eco"
        >
          <Phone size={18} weight="fill" /> 06 10 70 26 20
        </a>
      </motion.div>
    );
  }

  return (
    <div
      data-testid="eligibility-form"
      className="bg-white p-6 md:p-10 rounded-2xl shadow-[0_8px_30px_rgba(0,0,0,0.06)] border border-slate-100"
    >
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-blue-700">
          <Sparkle size={14} weight="fill" />
          Étude d'éligibilité
        </div>
        <div className="text-sm text-slate-500">
          Étape <span data-testid="current-step" className="font-semibold text-slate-900">{Math.min(step, TOTAL_STEPS + 1)}</span> / {TOTAL_STEPS + 1}
        </div>
      </div>

      <div className="h-2 bg-slate-100 rounded-full overflow-hidden mb-8">
        <motion.div
          data-testid="progress-bar"
          className="h-full bg-gradient-to-r from-blue-700 to-emerald-500"
          initial={{ width: 0 }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.5 }}
        />
      </div>

      <AnimatePresence mode="wait">
        {step === 1 && (
          <StepWrapper key="step-1">
            <StepTitle
              title="Quel est votre statut ?"
              subtitle="Cette information nous aide à identifier les solutions adaptées."
            />
            <div className="grid md:grid-cols-3 gap-3 mt-6">
              {STATUS_OPTIONS.map((opt) => (
                <OptionCard
                  key={opt.value}
                  testId={`status-${opt.value}`}
                  selected={data.status === opt.value}
                  onClick={() => choose("status", opt.value)}
                  icon={opt.icon}
                  label={opt.label}
                  desc={opt.desc}
                />
              ))}
            </div>
          </StepWrapper>
        )}

        {step === 2 && (
          <StepWrapper key="step-2">
            <StepTitle title="Quel est votre type de logement ?" />
            <div className="grid md:grid-cols-2 gap-3 mt-6">
              {HOUSING_OPTIONS.map((opt) => (
                <OptionCard
                  key={opt.value}
                  testId={`housing-${opt.value}`}
                  selected={data.housing_type === opt.value}
                  onClick={() => choose("housing_type", opt.value)}
                  icon={opt.icon}
                  label={opt.label}
                  desc={opt.desc}
                />
              ))}
            </div>
          </StepWrapper>
        )}

        {step === 3 && (
          <StepWrapper key="step-3">
            <StepTitle title="Quel est votre chauffage actuel ?" />
            <div className="grid md:grid-cols-2 gap-3 mt-6">
              {HEATING_OPTIONS.map((opt) => (
                <OptionCard
                  key={opt.value}
                  testId={`heating-${opt.value}`}
                  selected={data.heating_type === opt.value}
                  onClick={() => choose("heating_type", opt.value)}
                  icon={opt.icon}
                  label={opt.label}
                  desc={opt.desc}
                />
              ))}
            </div>
          </StepWrapper>
        )}

        {step === 4 && (
          <StepWrapper key="step-4">
            <StepTitle
              title="Quel est votre code postal ?"
              subtitle="Indispensable pour identifier les dispositifs et partenaires locaux."
            />
            <div className="mt-6 max-w-md">
              <input
                type="text"
                inputMode="numeric"
                data-testid="postal-code-input"
                value={data.postal_code}
                onChange={(e) =>
                  setData((d) => ({
                    ...d,
                    postal_code: e.target.value.replace(/\D/g, "").slice(0, 5),
                  }))
                }
                placeholder="Ex : 75001"
                className="w-full px-4 py-4 rounded-xl border-2 border-slate-200 focus:border-blue-600 focus:ring-2 focus:ring-blue-100 outline-none text-lg font-medium"
              />
            </div>
          </StepWrapper>
        )}

        {step === 5 && (
          <StepWrapper key="step-5">
            <StepTitle
              title="Votre revenu fiscal de référence (approximatif)"
              subtitle="(information indicative, non contractuelle)"
            />
            <div className="grid md:grid-cols-3 gap-3 mt-6">
              {INCOME_OPTIONS.map((opt) => (
                <OptionCard
                  key={opt.value}
                  testId={`income-${opt.value}`}
                  selected={data.income_range === opt.value}
                  onClick={() => choose("income_range", opt.value)}
                  icon={CurrencyEur}
                  label={opt.label}
                />
              ))}
            </div>
          </StepWrapper>
        )}

        {step === 6 && (
          <StepWrapper key="step-6">
            <div className="text-center mb-6">
              <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-emerald-100 flex items-center justify-center">
                <CheckCircle size={28} weight="fill" color="#10B981" />
              </div>
              <h3 className="font-heading text-2xl md:text-3xl font-bold text-slate-900 mb-2">
                Bonne nouvelle, votre profil semble correspondre.
              </h3>
              <p className="text-slate-600">
                Un expert va vous rappeler rapidement. Renseignez vos coordonnées
                pour être recontacté.
              </p>
            </div>
            <form onSubmit={onSubmit} className="space-y-4 max-w-lg mx-auto">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Nom
                </label>
                <input
                  type="text"
                  data-testid="name-input"
                  value={data.name}
                  onChange={(e) => setData((d) => ({ ...d, name: e.target.value }))}
                  required
                  placeholder="Votre nom"
                  className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-blue-600 focus:ring-2 focus:ring-blue-100 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Téléphone *
                </label>
                <input
                  type="tel"
                  data-testid="phone-input"
                  value={data.phone}
                  onChange={(e) => setData((d) => ({ ...d, phone: e.target.value }))}
                  required
                  placeholder="06 XX XX XX XX"
                  className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-blue-600 focus:ring-2 focus:ring-blue-100 outline-none"
                />
              </div>
              <button
                type="submit"
                data-testid="submit-eligibility-btn"
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl font-semibold btn-primary-eco disabled:opacity-60"
              >
                {loading ? "Envoi en cours..." : "Être rappelé rapidement"}
                {!loading && <ArrowRight size={18} weight="bold" />}
              </button>
              <p className="text-xs text-slate-700 text-center font-medium">
                Nous vous rappelons rapidement après votre demande
              </p>
              <p className="text-xs text-slate-500 text-center">
                Étude personnalisée et sans engagement. Aides disponibles selon votre situation.
              </p>
            </form>
          </StepWrapper>
        )}
      </AnimatePresence>

      <div className="mt-8 flex items-center justify-between">
        {step > 1 && step < 6 ? (
          <button
            data-testid="back-btn"
            onClick={back}
            className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-900"
          >
            <ArrowLeft size={16} /> Précédent
          </button>
        ) : (
          <span />
        )}
        {step === 4 && (
          <button
            data-testid="continue-cp-btn"
            onClick={next}
            disabled={!canProceedCP}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold btn-primary-eco disabled:opacity-50"
          >
            Continuer <ArrowRight size={16} weight="bold" />
          </button>
        )}
      </div>
    </div>
  );
}

function StepWrapper({ children }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.25 }}
    >
      {children}
    </motion.div>
  );
}

function StepTitle({ title, subtitle }) {
  return (
    <div>
      <h3 className="font-heading text-2xl md:text-3xl font-bold text-slate-900">{title}</h3>
      {subtitle && <p className="text-slate-500 mt-2 text-sm">{subtitle}</p>}
    </div>
  );
}

function OptionCard({ testId, selected, onClick, icon: Icon, label, desc }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={`text-left p-5 rounded-xl border-2 transition-all ${
        selected
          ? "border-blue-600 bg-blue-50 ring-2 ring-blue-100"
          : "border-slate-200 bg-white hover:border-blue-400 hover:bg-slate-50"
      }`}
    >
      <div className="flex items-center gap-3 mb-1">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
          selected ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600"
        }`}>
          <Icon size={18} weight={selected ? "fill" : "regular"} />
        </div>
        <span className="font-semibold text-slate-900">{label}</span>
      </div>
      {desc && <p className="text-xs text-slate-500 ml-12">{desc}</p>}
    </button>
  );
}
